import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useGetFormData } from 'cng-common/features/form'
import { TFormValidationResult } from 'cng-common/features/form/models'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { Translation } from 'cng-common/features/translations'
import { Actions } from 'core-components/library/modalDialog'

import { useCreateEspRequestMutation, useGetEspPartnerQuery } from '../../api/espApi'
import { ESP_TRANSLATION_KEYS as KEYS } from '../../configs/translations'
import { ESP_REQUEST_FORM_KEY } from '../../constants'
import { getEspAccountInfo } from '../../lib/getEspAccountInfo'
import { TEspRequestType } from '../../model/types'

import { getInitialEspRequestForm, IEspRequestFormData } from './getInitialEspRequestForm'

const FORM_KEY = ESP_REQUEST_FORM_KEY

export interface IEspRequestFormFooterProps {
  partnerId: string
  mode: TEspRequestType
  onClose: () => void
}

/**
 * The modal's fixed footer. It reads the same form key the content writes, so the
 * buttons stay pinned while only the fields scroll (mdfActivityDetailsModal pattern).
 */
export const EspRequestFormFooter = ({ partnerId, mode, onClose }: IEspRequestFormFooterProps) => {
  const partnerData = useSelector(selectPartner360Data)
  const { data: espPartner } = useGetEspPartnerQuery({ partnerId })
  const partnerRequests = espPartner?.requests
  const [createRequest, { isLoading: isSubmitting }] = useCreateEspRequestMutation()

  const initialForm = useMemo(
    () => getInitialEspRequestForm(partnerRequests?.[0] ?? null),
    [partnerRequests]
  )

  const [{ formData }] = useGetFormData<IEspRequestFormData>(FORM_KEY, initialForm)

  const handleSubmit = useCallback(async () => {
    const validation = FormValidationBuilder.build(FORM_KEY)
    const validationCalls = [validation.call('sponsorName', formData.sponsorName)]

    if (mode === 'change') {
      validationCalls.push(validation.call('justification', formData.justification))
    }

    const errors = await Promise.all(validationCalls)

    if (errors.some((error): error is TFormValidationResult => Boolean(error))) {
      return
    }

    const { sponsorName, sponsorEmail, justification, ...additionalInfo } = formData
    const { partnerName, partnerCountry, accountClassification, accountSegment } =
      getEspAccountInfo(partnerData)

    // The submitter comes from the token on the service, never from the client.
    try {
      await createRequest({
        partnerId,
        type: mode,
        sponsorName: sponsorName.trim(),
        sponsorEmail: sponsorEmail.trim() || null,
        accountInfo: { partnerName, partnerCountry, accountClassification, accountSegment },
        additionalInfo,
        ...(justification.trim() ? { justification: justification.trim() } : {}),
      }).unwrap()

      onClose()
    } catch {
      // the api layer already surfaced the error toast; keep the form open
    }
  }, [createRequest, formData, mode, onClose, partnerData, partnerId])

  return (
    <Actions
      items={[
        {
          id: 'cancel',
          label: <Translation text={KEYS.requestForm.cancel} span />,
          variant: 'text',
          color: 'danger',
          disabled: isSubmitting,
          onClick: onClose,
        },
        {
          id: 'submit',
          label: <Translation text={KEYS.requestForm.submit} span />,
          variant: 'contained',
          color: 'primary',
          disabled: isSubmitting,
          onClick: handleSubmit,
        },
      ]}
    />
  )
}

EspRequestFormFooter.displayName = 'EspRequestFormFooter'
